import React, {useEffect, useState, useCallback, useContext, useMemo} from "react";
import styled from "styled-components";
import PropTypes from "prop-types";
import dayjs from "dayjs";
import {FormProvider, useForm} from "react-hook-form";

// Hooks
import useApi from "../../hooks/useApi.js";

// Contexts
import {SettingsContext} from "../../contexts/settings.js";

// Utils
import {prettyDateInUserTimezone} from "../../utils/helpers.js";

// Components
import Modal from "../../components/Modal.js";
import BaseTable from "../../components/BaseTable.js";
import InputDate from "../../components/form/InputDate.js";

// Style
import {
  HeadingCenter,
  Text,
  Inline,
  HeadingSmall,
  NotLoaded,
  Loader,
  Abbr
} from "../../style/components/general.js";
import {pad} from "../../style/components/variables.js";
import ChartPi from "../../components/ChartPi.js";

const ModalUserStats = ({visible, setVisible, user}) => {
  const [stats, setStats] = useState();
  const [loading, setLoading] = useState(true);

  const form = useForm();
  const {watch} = form;

  const {settings} = useContext(SettingsContext);

  const startDate = watch("startDate");
  const endDate = watch("endDate");

  const {api: apiStats} = useApi("user-details");

  const fetchStats = useCallback(() => {
    if (!user?.publicId) return;
    const params = {};
    if (startDate) params.startDate = dayjs(startDate).format("YYYY-MM-DD");
    if (endDate) params.endDate = dayjs(endDate).format("YYYY-MM-DD");

    apiStats
      .callGet(user.publicId, params)
      .then(({status, data}) => {
        if (status === 200) setStats(data);
      })
      .finally(() => setLoading(false));
  }, [user, startDate, endDate, apiStats]);

  useEffect(() => {
    if (visible) fetchStats();
  }, [visible, fetchStats]);

  const checksheetHistoryFormatted = useMemo(() => {
    if (!stats?.checksheetHistory) return [];
    return stats.checksheetHistory.map(({facility, checksheet, dateDue, completedAt}) => ({
      facility: <Abbr title={facility.name}>{facility.name}</Abbr>,
      checksheet: (
        <ChecksheetLink href={`/facilities/${facility.slug}/checksheets#${checksheet.id}`}>
          {checksheet.name}&nbsp; ({checksheet.frequency})
        </ChecksheetLink>
      ),
      dateDue: dateDue
        ? prettyDateInUserTimezone(dateDue, settings?.timezone, "MMM D YYYY, h:mm A")
        : "N/A",
      completedAt: prettyDateInUserTimezone(completedAt, settings?.timezone, "MMM D YYYY, h:mm A")
    }));
  }, [settings, stats]);

  const piData = useMemo(
    () =>
      stats
        ? [
            {
              value: stats.completionStats?.percentageOnTime ?? 0,
              label: "On-Time",
              themeColor: "success"
            },
            {
              value: stats.completionStats?.percentageLate ?? 0,
              label: "Late",
              themeColor: "alert"
            }
          ].filter(({value}) => value > 0)
        : [],
    [stats]
  );

  const showPi = useMemo(() => piData.some(({value}) => value > 0), [piData]);

  return (
    <Modal visible={visible} setVisible={setVisible}>
      <HeadingCenter>User Stats</HeadingCenter>

      <Text>
        <strong>User:</strong> {user?.firstName} {user?.lastName}
      </Text>

      {!loading ? (
        <>
          <Text>
            <strong>Last Login:</strong>{" "}
            {stats?.lastLogin
              ? prettyDateInUserTimezone(stats.lastLogin, settings?.timezone, "MMM D YYYY, h:mm A")
              : "N/A"}
          </Text>

          <hr />

          <FormProvider {...form}>
            <Inline>
              <InputDate name="startDate" label="Start Date" />
              <InputDate name="endDate" label="End Date" />
            </Inline>
          </FormProvider>

          {stats && (
            <StatsWrapper>
              <InlineTop>
                <StatWrapper>
                  <Text>
                    <strong>On-Time:</strong> {stats.completionStats?.onTimeCount}
                  </Text>
                  <Text>
                    <strong>Late:</strong> {stats.completionStats?.lateCount}
                  </Text>
                </StatWrapper>
                {showPi && <ChartPi data={piData} radius={80} showColors showValue />}
              </InlineTop>

              <br />
              <br />
              <StyledHeadingSmall>Checksheet History</StyledHeadingSmall>
              <BaseTable
                headings={{
                  facility: {header: "Facility"},
                  checksheet: {header: "Checksheet"},
                  frequency: {header: "Frequency"},
                  dateDue: {header: "Date Due"},
                  completedAt: {header: "Completed At"}
                }}
                data={checksheetHistoryFormatted}
              />
            </StatsWrapper>
          )}
        </>
      ) : (
        <NotLoaded>
          <Loader />
        </NotLoaded>
      )}
    </Modal>
  );
};

ModalUserStats.propTypes = {
  visible: PropTypes.bool.isRequired,
  setVisible: PropTypes.func.isRequired,
  user: PropTypes.object
};

const StatsWrapper = styled.div`
  margin-top: ${pad * 3}px;
`;

const StyledHeadingSmall = styled(HeadingSmall)`
  margin-bottom: ${pad}px;
`;

const InlineTop = styled(Inline)`
  align-items: start;
`;

const StatWrapper = styled.div`
  margin-top: ${pad * 2}px;
`;

const ChecksheetLink = styled.a`
  border: 0;
  background: 0;
  text-decoration: underline;

  svg {
    fill: ${props => props.theme.secondary};
  }
`;

export default ModalUserStats;
